<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import moment from "moment";
import DateRangePicker from "daterangepicker";

import { ref, onMounted, computed } from "vue";
import { useProjectStore } from "@/stores/projectStore";
import ProjectReportTable from "./project-report-table.vue";

const title = ref("Project Report");
const text = ref("Projects");
const text1 = ref("Project Report");

const projectStore = useProjectStore();

const loading = computed(
  () => projectStore.loading || projectStore.reportLoading
);
const items = computed(() => projectStore.items);
const meta = computed(() => projectStore.meta);
const summary = computed(() => projectStore.reportSummary);
const tasksChart = computed(() => projectStore.tasksChart);

const sortLabel = ref("Last 7 Days");
const dateRangeInput = ref(null);

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function applyFilters(page = 1) {
  projectStore.list({ page, force: true });
  projectStore.fetchReport({ force: true });
}

function handlePageChange(page) {
  applyFilters(page);
}

function setPriority(priority) {
  projectStore.filters.priority = priority;
  applyFilters(1);
}

function setStatus(status) {
  projectStore.filters.status = status;
  applyFilters(1);
}

function setSort(sort, label) {
  projectStore.filters.sort = sort;
  sortLabel.value = label;
  applyFilters(1);
}

// simple progression helper for the progress bars
function progressPercent(count) {
  const total = summary.value.total_projects || 1;
  return Math.round((Number(count || 0) / total) * 100);
}

const chartOptions = computed(() => ({
  chart: { type: "pie" },
  labels: tasksChart.value.labels,
  legend: {
    position: "bottom",
  },
}));

const chartSeries = computed(() => tasksChart.value.series || [0, 0, 0, 0]);

function exportAs(format) {
  projectStore.downloadReport(format);
}

onMounted(async () => {
  const start = moment().subtract(6, "days");
  const end = moment();

  projectStore.filters.date_from = start.format("YYYY-MM-DD");
  projectStore.filters.date_to = end.format("YYYY-MM-DD");

  if (dateRangeInput.value) {
    new DateRangePicker(
      dateRangeInput.value,
      {
        startDate: start,
        endDate: end,
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [
            moment().subtract(1, "days"),
            moment().subtract(1, "days"),
          ],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      function booking_range(start, end) {
        if (dateRangeInput.value) {
          dateRangeInput.value.value =
            start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
        }
        projectStore.filters.date_from = start.format("YYYY-MM-DD");
        projectStore.filters.date_to = end.format("YYYY-MM-DD");
        applyFilters(1);
      }
    );

    dateRangeInput.value.value =
      start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
  }

  await projectStore.ensureList({ page: 1 });
  await projectStore.ensureReport();
});
</script>

<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportAs('pdf')"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportAs('xlsx')"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="row dq-stat-grid">
        <!-- Summary cards -->
        <div class="col-lg-6 col-md-6 d-flex">
          <div class="row flex-fill">
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div>
                    <div class="mb-2">
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Total Projects</span
                      >
                      <h5>{{ summary.total_projects }}</h5>
                    </div>
                    <div
                      class="progress"
                      role="progressbar"
                      style="width: 100%; height: 5px"
                    >
                      <div
                        class="progress-bar bg-pink"
                        :style="{
                          width: progressPercent(summary.total_projects) + '%',
                        }"
                      ></div>
                    </div>
                  </div>
                  <div class="d-flex mt-2">
                    <p
                      class="fs-12 fw-normal d-flex align-items-center text-truncate mb-0"
                    >
                      Projects within selected filters
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div>
                    <div class="mb-2">
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Completed Projects</span
                      >
                      <h5>{{ summary.completed_projects }}</h5>
                    </div>
                    <div class="progress" style="width: 100%; height: 5px">
                      <div
                        class="progress-bar bg-success"
                        :style="{
                          width:
                            progressPercent(summary.completed_projects) + '%',
                        }"
                      ></div>
                    </div>
                  </div>
                  <div class="d-flex mt-2">
                    <p
                      class="fs-12 fw-normal d-flex align-items-center text-truncate mb-0"
                    >
                      Projects where all tasks are completed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div>
                    <div class="mb-2">
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Pending Projects</span
                      >
                      <h5>{{ summary.pending_projects }}</h5>
                    </div>
                    <div class="progress" style="width: 100%; height: 5px">
                      <div
                        class="progress-bar bg-danger"
                        :style="{
                          width:
                            progressPercent(summary.pending_projects) + '%',
                        }"
                      ></div>
                    </div>
                  </div>
                  <div class="d-flex mt-2">
                    <p
                      class="fs-12 fw-normal d-flex align-items-center text-truncate mb-0"
                    >
                      Projects with work still in progress
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div>
                    <div class="mb-2">
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >New Projects</span
                      >
                      <h5>{{ summary.new_projects }}</h5>
                    </div>
                    <div class="progress" style="width: 100%; height: 5px">
                      <div
                        class="progress-bar bg-purple"
                        :style="{
                          width: progressPercent(summary.new_projects) + '%',
                        }"
                      ></div>
                    </div>
                  </div>
                  <div class="d-flex mt-2">
                    <p
                      class="fs-12 fw-normal d-flex align-items-center text-truncate mb-0"
                    >
                      Created in the last 30 days
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Projects by tasks donut -->
        <div class="col-lg-6 col-md-6 d-flex">
          <div class="card flex-fill">
            <div class="card-header border-0">
              <div
                class="d-flex flex-wrap justify-content-between align-items-center row-gap-3"
              >
                <div class="d-flex align-items-center">
                  <span class="me-2"
                    ><i class="ti ti-chart-pie text-danger"></i
                  ></span>
                  <h5>Projects By Tasks</h5>
                </div>
              </div>
            </div>
            <div class="card-body pt-0">
              <div class="row align-items-center">
                <div class="col-md-6 d-flex justify-content-center">
                  <div id="project-report">
                    <apexchart
                      type="pie"
                      height="190"
                      :options="chartOptions"
                      :series="chartSeries"
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="row gy-4">
                    <div class="col-md-6">
                      <p
                        class="fs-16 project-report-badge-blue fw-normal mb-0 text-gray-5"
                      >
                        Pending
                      </p>
                      <p class="fs-20 fw-bold text-dark">
                        {{ tasksChart.series[0] || 0 }}
                      </p>
                    </div>
                    <div class="col-md-6">
                      <p
                        class="fs-16 project-report-badge-purple mb-0 fw-normal text-gray-5"
                      >
                        On Hold
                      </p>
                      <p class="fs-20 fw-bold text-dark">
                        {{ tasksChart.series[1] || 0 }}
                      </p>
                    </div>
                    <div class="col-md-6">
                      <p
                        class="fs-16 project-report-badge-warning mb-0 fw-normal text-gray-5"
                      >
                        In Progress
                      </p>
                      <p class="fs-20 fw-bold text-dark">
                        {{ tasksChart.series[2] || 0 }}
                      </p>
                    </div>
                    <div class="col-md-6">
                      <p
                        class="fs-16 project-report-badge-success mb-0 fw-normal text-gray-5"
                      >
                        Completed
                      </p>
                      <p class="fs-20 fw-bold text-dark">
                        {{ tasksChart.series[3] || 0 }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Project list -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Project List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3 dq-list-toolbar"
          >
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                  readonly
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <!-- Priority filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ projectStore.filters.priority || "Select Priority" }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setPriority('Low')"
                    >Low</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setPriority('Medium')"
                    >Medium</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setPriority('High')"
                    >High</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setPriority('')"
                    >All</a
                  >
                </li>
              </ul>
            </div>

            <!-- Status filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ projectStore.filters.status || "Select Status" }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Active')"
                    >Active</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Inactive')"
                    >Inactive</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('')"
                    >All</a
                  >
                </li>
              </ul>
            </div>

            <!-- Sort -->
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort By : {{ sortLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('recent', 'Recently Added')"
                    >Recently Added</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('asc', 'Name (A to Z)')"
                    >Name (A to Z)</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('desc', 'Name (Z to A)')"
                    >Name (Z to A)</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('oldest', 'Oldest First')"
                    >Oldest First</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <ProjectReportTable
              :items="items"
              :loading="loading"
              :meta="meta"
              @page-change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-list-toolbar {
    align-items: stretch !important;
  }

  .dq-list-toolbar > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-list-toolbar .dropdown,
  .dq-list-toolbar .dropdown-toggle,
  .dq-list-toolbar .form-control {
    width: 100%;
  }

  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>


